import { makeSystemCableSpecGetter } from "../cable-spec";
import type { DefLookup } from "../catalog";
import { dcBreakerInterruptA, dcBreakerSpec } from "../dc-breaker";
import { dcBreakerConductorRatings } from "../dc-breaker-conductors";
import { buildDcCircuitTopology } from "../dc-circuit-topology";
import type { Issue, System } from "../types";
import { buildGraph, reachable, v } from "./graph";
import { getBatteryGroups } from "./protection";

export function runDcBreakerDiagnostics(system: System, lookup: DefLookup, targetPct = 3): Issue[] {
  const nodes = system.nodes.filter(node => node.kind === "component" && lookup(node.definitionId, node.customDefinition)?.dcBreaker);
  if (!nodes.length) return [];
  const issues: Issue[] = [];
  const ratings = dcBreakerConductorRatings(system, lookup);
  const specOf = makeSystemCableSpecGetter(system, lookup, undefined, targetPct);
  for (const cable of system.cables) {
    const protection = ratings.get(cable.id);
    if (!protection) continue;
    const spec = specOf(cable);
    if (spec.sizingVerificationRequired || protection.ratingA <= spec.wire.ampacity) continue;
    issues.push({ code: "dc-breaker-exceeds-wire", severity: "critical",
      title: { key: "diag.dc-breaker-exceeds-wire.title", vars: { rating: protection.ratingA } },
      message: { key: "diag.dc-breaker-exceeds-wire.message", vars: { mm2: spec.wire.mm2, ampacity: spec.wire.ampacity } },
      fix: { key: "diag.dc-breaker-exceeds-wire.fix" }, cableIds: [cable.id], nodeIds: protection.nodeIds });
  }
  const groups = getBatteryGroups(system, lookup);
  const topology = buildDcCircuitTopology(system, lookup);
  const openGraph = buildGraph(system, lookup, { excludeFused: true, excludeSense: true });
  for (const node of nodes) {
    if (node.kind !== "component") continue;
    const def = lookup(node.definitionId, node.customDefinition)!;
    const breaker = dcBreakerSpec(def);
    const label = node.label ?? def.shortName ?? def.name;
    const attached = system.cables.filter(c => c.fromNodeId === node.id || c.toNodeId === node.id).map(c => c.id);
    if (!breaker) {
      issues.push({ code: "dc-breaker-unverified", severity: "warning", title: { key: "diag.dc-breaker-unverified.title", vars: { label } },
        message: { key: "diag.dc-breaker-unverified.message" }, fix: { key: "diag.dc-breaker-unverified.fix" }, nodeIds: [node.id], cableIds: attached });
      continue;
    }
    const line = v(node.id, breaker.inputTerminalId), load = v(node.id, breaker.outputTerminalId);
    const relevant = groups.filter(group => [...group.positiveVerts, ...group.seriesVerts].some(vertex => topology.railId(vertex) === topology.railId(line)));
    for (const group of relevant) {
      const lineReach = reachable(openGraph, line), loadReach = reachable(openGraph, load);
      if (group.positiveVerts.some(vertex => loadReach.has(vertex)) && !group.positiveVerts.some(vertex => lineReach.has(vertex))) {
        issues.push({ code: "dc-breaker-reversed", severity: "warning", title: { key: "diag.dc-breaker-reversed.title", vars: { label } },
          message: { key: "diag.dc-breaker-reversed.message" }, fix: { key: "diag.dc-breaker-reversed.fix" }, nodeIds: [node.id], cableIds: attached });
      }
      const voltage = group.nominalV;
      if (voltage != null && breaker.dcMaxVoltage != null && voltage > breaker.dcMaxVoltage) {
        issues.push({ code: "dc-breaker-voltage", severity: "critical", title: { key: "diag.dc-breaker-voltage.title", vars: { label } },
          message: { key: "diag.dc-breaker-voltage.message", vars: { voltage, maximum: breaker.dcMaxVoltage } },
          fix: { key: "diag.dc-breaker-voltage.fix" }, nodeIds: [node.id, ...group.batteryNodeIds], cableIds: attached });
      }
      const interruptA = voltage != null ? dcBreakerInterruptA(def, voltage) : undefined;
      if (interruptA != null && group.faultCurrent.status === "known" && group.faultCurrent.value > interruptA) {
        issues.push({ code: "dc-breaker-interrupt-capacity", severity: "critical", title: { key: "diag.dc-breaker-interrupt-capacity.title", vars: { label } },
          message: { key: "diag.dc-breaker-interrupt-capacity.message", vars: { fault: group.faultCurrent.value, rating: interruptA } },
          fix: { key: "diag.dc-breaker-interrupt-capacity.fix" }, nodeIds: [node.id, ...group.batteryNodeIds], cableIds: attached });
      }
    }
    // A nominal bank voltage is not its verified maximum charging voltage.
    // Declared AIC/fault inputs can prove an inadequacy above, but cannot certify
    // interruption, upstream placement or time-current coordination by themselves.
    issues.push({ code: "dc-breaker-unverified", severity: "warning", title: { key: "diag.dc-breaker-unverified.title", vars: { label } },
      message: { key: "diag.dc-breaker-unverified.message" }, fix: { key: "diag.dc-breaker-unverified.fix" },
      nodeIds: [node.id, ...new Set(relevant.flatMap(group => group.batteryNodeIds))], cableIds: attached });
  }
  return issues;
}
